import { useState, useEffect, useCallback, useMemo } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  Plus, Bell, Clock, Calendar, Trash2, Check,
  Repeat, AlertCircle, Loader2, BellRing, Flag, BellOff, Timer
} from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/authFetch";
import NotificationBell from "@/components/shared/NotificationBell";  // FaseC C1

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Security audit (FIN-2 T1, 2026-04-30) — sections legacy.
 *
 * Backend já blindado pela SEC-1. Sem JWT, fetch retorna 401 e UI quebra.
 *
 * | Endpoint chamado              | authFetch usado? | Notas         |
 * |-------------------------------|------------------|---------------|
 * | GET  /api/reminders           | ❌ → ✅ (T2)     | listar        |
 * | POST /api/reminders           | ❌ → ✅ (T2)     | criar         |
 * | POST /api/reminders/complete  | ❌ → ✅ (T2)     | concluir      |
 * | DEL  /api/reminders/:id       | ❌ → ✅ (T2)     | deletar       |
 */

function StatsCard({ icon: Icon, value, label, color }) {
  return (
    <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
      className="bg-[#0d1220] border border-white/[0.06] rounded-2xl p-4 hover:border-white/10 transition-all">
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${color}12` }}>
          <Icon className="w-5 h-5" style={{ color }} />
        </div>
        <div>
          <h3 className="text-2xl font-bold text-white tabular-nums">{value}</h3>
          <p className="text-xs text-slate-500">{label}</p>
        </div>
      </div>
    </motion.div>
  );
}

function TimelineGroup({ title, icon: Icon, color, reminders, onComplete, onDelete }) {
  if (reminders.length === 0) return null;
  return (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-3 px-1">
        <Icon className="w-4 h-4" style={{ color }} />
        <h3 className="text-sm font-semibold" style={{ color }}>{title}</h3>
        <span className="text-xs text-slate-600 ml-1">({reminders.length})</span>
      </div>
      <div className="space-y-2">
        <AnimatePresence mode="popLayout">
          {reminders.map(r => (
            <ReminderCard key={r.id} reminder={r} onComplete={onComplete} onDelete={onDelete} />
          ))}
        </AnimatePresence>
      </div>
    </div>
  );
}

function ReminderCard({ reminder, onComplete, onDelete }) {
  const isOverdue = reminder.datetime_scheduled && new Date(reminder.datetime_scheduled) < new Date() && !reminder.is_completed;

  const formatDateTime = (dateStr) => {
    if (!dateStr) return null;
    const d = new Date(dateStr);
    const now = new Date();
    const diffMs = d - now;
    const diffH = Math.floor(diffMs / 3600000);
    const diffD = Math.floor(diffMs / 86400000);
    const timeStr = d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
    const dateFormatted = d.toLocaleDateString("pt-BR", { day: "2-digit", month: "short" });

    if (diffMs < 0) return { label: `${dateFormatted} ${timeStr}`, relative: `${Math.abs(diffD) || 1}d atrasado`, isUrgent: true };
    if (diffH < 1) return { label: timeStr, relative: `${Math.max(1, Math.floor(diffMs / 60000))} min`, isUrgent: true };
    if (diffH < 24) return { label: timeStr, relative: `${diffH}h`, isUrgent: diffH < 3 };
    if (diffD < 7) return { label: `${dateFormatted} ${timeStr}`, relative: `${diffD}d`, isUrgent: false };
    return { label: `${dateFormatted} ${timeStr}`, relative: null, isUrgent: false };
  };

  const timeInfo = reminder.datetime_scheduled ? formatDateTime(reminder.datetime_scheduled) : null;
  const recurrenceLabels = { daily: "Diário", weekly: "Semanal", monthly: "Mensal" };

  return (
    <motion.div layout initial={{ opacity: 0, x: -8 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: 8 }}
      className={`flex items-start gap-3 px-4 py-3.5 bg-[#0d1220] border rounded-xl transition-all group hover:bg-white/[0.015]
        ${reminder.is_completed ? "border-white/[0.04] opacity-40" : isOverdue ? "border-red-500/20" : "border-white/[0.06]"}`}
      data-testid={`reminder-card-${reminder.id}`}>
      <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0
        ${isOverdue ? "bg-red-500/10" : "bg-purple-500/10"}`}>
        {isOverdue ? <AlertCircle className="w-4 h-4 text-red-400" /> : <BellRing className="w-4 h-4 text-purple-400" />}
      </div>
      <div className="flex-1 min-w-0">
        <h4 className={`text-sm font-medium ${reminder.is_completed ? "line-through text-slate-500" : "text-white"}`}>{reminder.message}</h4>
        <div className="flex items-center gap-2 mt-2 flex-wrap">
          {timeInfo && (
            <span className={`flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md ${timeInfo.isUrgent && !reminder.is_completed ? "bg-red-500/10 text-red-400" : "bg-white/[0.03] text-slate-500"}`}>
              <Clock className="w-3 h-3" /> {timeInfo.label}
            </span>
          )}
          {timeInfo?.relative && !reminder.is_completed && (
            <span className={`text-[11px] px-2 py-0.5 rounded-md font-medium ${timeInfo.isUrgent ? "bg-red-500/10 text-red-400" : "bg-cyan-500/10 text-cyan-400"}`}>
              {isOverdue ? timeInfo.relative : `Em ${timeInfo.relative}`}
            </span>
          )}
          {reminder.recurrence && reminder.recurrence !== "none" && (
            <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-purple-500/10 text-purple-400">
              <Repeat className="w-3 h-3" /> {recurrenceLabels[reminder.recurrence] || reminder.recurrence}
            </span>
          )}
          {reminder.priority === "high" && (
            <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-md bg-red-500/10 text-red-400">
              <Flag className="w-3 h-3" /> Urgente
            </span>
          )}
        </div>
      </div>
      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {!reminder.is_completed && (
          <button onClick={() => onComplete(reminder)} className="p-1.5 rounded-lg text-slate-600 hover:text-emerald-400 hover:bg-emerald-500/10 transition-colors" data-testid={`complete-reminder-${reminder.id}`}>
            <Check className="w-4 h-4" />
          </button>
        )}
        <button onClick={() => onDelete(reminder)} className="p-1.5 rounded-lg text-slate-600 hover:text-red-400 hover:bg-red-500/10 transition-colors" data-testid={`delete-reminder-${reminder.id}`}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </motion.div>
  );
}

function CreateReminderModal({ isOpen, onClose, onSubmit }) {
  const [message, setMessage] = useState("");
  const [datetime, setDatetime] = useState("");
  const [recurrence, setRecurrence] = useState("none");
  const [priority, setPriority] = useState("medium");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) { setMessage(""); setDatetime(""); setRecurrence("none"); setPriority("medium"); }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault(); if (!message.trim()) return;
    setIsSubmitting(true);
    await onSubmit({ message, datetime_scheduled: datetime || null, recurrence, priority });
    setIsSubmitting(false); onClose();
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4" onClick={onClose}>
          <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }}
            onClick={(e) => e.stopPropagation()} className="bg-[#0d1220] border border-white/[0.08] rounded-2xl p-6 safe-modal-pb w-full max-w-md">
            <h3 className="text-lg font-bold text-white mb-5">Novo Lembrete</h3>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Mensagem</label>
                <input type="text" value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Do que quer ser lembrado?"
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white placeholder-slate-700 focus:outline-none focus:border-purple-500/30 transition-colors" autoFocus />
              </div>
              <div>
                <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Data e Hora</label>
                <input type="datetime-local" value={datetime} onChange={(e) => setDatetime(e.target.value)}
                  className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:border-purple-500/30 transition-colors" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Repetir</label>
                  <select value={recurrence} onChange={(e) => setRecurrence(e.target.value)}
                    className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:border-purple-500/30 transition-colors">
                    <option value="none">Não repetir</option><option value="daily">Diário</option>
                    <option value="weekly">Semanal</option><option value="monthly">Mensal</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs text-slate-500 mb-1.5 uppercase tracking-wider">Prioridade</label>
                  <select value={priority} onChange={(e) => setPriority(e.target.value)}
                    className="w-full px-4 py-3 bg-white/[0.03] border border-white/[0.06] rounded-xl text-white focus:outline-none focus:border-purple-500/30 transition-colors">
                    <option value="low">Baixa</option><option value="medium">Média</option><option value="high">Alta</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-3 pt-2">
                <button type="button" onClick={onClose} className="flex-1 px-4 py-3 rounded-xl border border-white/[0.06] text-slate-400 hover:text-white hover:bg-white/5 transition-colors">Cancelar</button>
                <button type="submit" disabled={!message.trim() || isSubmitting}
                  className="flex-1 px-4 py-3 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/15 disabled:opacity-40 transition-all flex items-center justify-center gap-2 font-medium">
                  {isSubmitting && <Loader2 className="w-4 h-4 animate-spin" />} Criar
                </button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default function RemindersSection() {
  const [reminders, setReminders] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState("active");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const fetchReminders = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/reminders?include_completed=true`);
      if (res.ok) { const data = await res.json(); setReminders(data.reminders || []); }
    } catch (err) { console.error("Error:", err); }
    finally { setIsLoading(false); }
  }, []);

  useEffect(() => { fetchReminders(); }, [fetchReminders]);

  const handleCreate = async (data) => {
    try {
      const res = await authFetch(`${API_URL}/api/reminders`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(data) });
      if (res.ok) { toast.success("Lembrete criado!"); fetchReminders(); }
    } catch (err) { toast.error("Erro"); }
  };

  const handleComplete = async (reminder) => {
    try {
      const res = await authFetch(`${API_URL}/api/reminders/complete`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ id: reminder.id }),
      });
      if (res.ok) {
        setReminders(prev => prev.map(r => r.id === reminder.id ? { ...r, is_completed: true } : r));
        toast.success("Concluído!");
      }
    } catch (err) { console.error(err); }
  };

  const handleDelete = async (reminder) => {
    if (!window.confirm("Excluir?")) return;
    try {
      const res = await authFetch(`${API_URL}/api/reminders/${reminder.id}`, { method: "DELETE" });
      if (res.ok) { setReminders(prev => prev.filter(r => r.id !== reminder.id)); toast.success("Excluído"); }
    } catch (err) { console.error(err); }
  };

  const filtered = useMemo(() => {
    let list = reminders;
    if (filter === "active") list = reminders.filter(r => !r.is_completed);
    else if (filter === "completed") list = reminders.filter(r => r.is_completed);
    return list;
  }, [reminders, filter]);

  const grouped = useMemo(() => {
    const now = new Date();
    const todayEnd = new Date(now); todayEnd.setHours(23, 59, 59, 999);
    const tomorrowEnd = new Date(todayEnd); tomorrowEnd.setDate(tomorrowEnd.getDate() + 1);
    const weekEnd = new Date(now); weekEnd.setDate(weekEnd.getDate() + 7);

    const overdue = []; const today = []; const tomorrow = []; const upcoming = []; const noDate = [];
    for (const r of filtered) {
      if (r.is_completed) { upcoming.push(r); continue; }
      const dt = r.datetime_scheduled ? new Date(r.datetime_scheduled) : null;
      if (!dt) { noDate.push(r); }
      else if (dt < now) { overdue.push(r); }
      else if (dt <= todayEnd) { today.push(r); }
      else if (dt <= tomorrowEnd) { tomorrow.push(r); }
      else { upcoming.push(r); }
    }
    return { overdue, today, tomorrow, upcoming, noDate };
  }, [filtered]);

  const stats = useMemo(() => ({
    total: reminders.length,
    active: reminders.filter(r => !r.is_completed).length,
    overdue: reminders.filter(r => r.datetime_scheduled && new Date(r.datetime_scheduled) < new Date() && !r.is_completed).length,
    completed: reminders.filter(r => r.is_completed).length,
  }), [reminders]);

  return (
    <div className="p-6 md:p-8 pb-32 max-w-5xl mx-auto" data-testid="reminders-section">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-white tracking-wide">Lembretes</h1>
          <p className="text-sm text-slate-500 mt-1">{stats.active} ativo{stats.active !== 1 ? "s" : ""}</p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <NotificationBell />
          <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setIsModalOpen(true)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 hover:bg-purple-500/15 transition-all text-sm font-medium" data-testid="create-reminder-btn">
            <Plus className="w-4 h-4" /> Novo Lembrete
          </motion.button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-6">
        <StatsCard icon={Bell} value={stats.active} label="Ativos" color="#a855f7" />
        <StatsCard icon={AlertCircle} value={stats.overdue} label="Atrasados" color="#ef4444" />
        <StatsCard icon={Timer} value={grouped.today.length} label="Hoje" color="#06b6d4" />
        <StatsCard icon={Check} value={stats.completed} label="Concluídos" color="#22c55e" />
      </div>

      {/* Filters */}
      <div className="flex gap-1.5 mb-5">
        {[{ id: "active", label: "Ativos" }, { id: "completed", label: "Concluídos" }, { id: "all", label: "Todos" }].map(f => (
          <button key={f.id} onClick={() => setFilter(f.id)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f.id ? "bg-purple-500/10 text-purple-400 border border-purple-500/20" : "text-slate-500 hover:text-slate-300 border border-transparent"}`}
            data-testid={`reminder-filter-${f.id}`}>{f.label}</button>
        ))}
      </div>

      {/* Timeline Groups */}
      {isLoading ? (
        <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 text-purple-400 animate-spin" /></div>
      ) : filtered.length === 0 ? (
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="text-center py-16">
          <div className="w-14 h-14 rounded-2xl bg-purple-500/10 flex items-center justify-center mx-auto mb-4">
            <Bell className="w-7 h-7 text-purple-400" />
          </div>
          <h3 className="text-lg font-medium text-white mb-1.5">Nenhum lembrete</h3>
          <p className="text-slate-600 text-sm mb-5">Crie um para não esquecer de nada</p>
          <button onClick={() => setIsModalOpen(true)} className="px-5 py-2.5 rounded-xl bg-purple-500/10 border border-purple-500/20 text-purple-400 text-sm hover:bg-purple-500/15 transition-all">Criar Lembrete</button>
        </motion.div>
      ) : (
        <div data-testid="reminders-timeline">
          <TimelineGroup title="Atrasados" icon={AlertCircle} color="#ef4444" reminders={grouped.overdue} onComplete={handleComplete} onDelete={handleDelete} />
          <TimelineGroup title="Hoje" icon={Timer} color="#06b6d4" reminders={grouped.today} onComplete={handleComplete} onDelete={handleDelete} />
          <TimelineGroup title="Amanhã" icon={Calendar} color="#f59e0b" reminders={grouped.tomorrow} onComplete={handleComplete} onDelete={handleDelete} />
          <TimelineGroup title="Próximos" icon={Clock} color="#a855f7" reminders={grouped.upcoming} onComplete={handleComplete} onDelete={handleDelete} />
          <TimelineGroup title="Sem data" icon={BellOff} color="#64748b" reminders={grouped.noDate} onComplete={handleComplete} onDelete={handleDelete} />
        </div>
      )}

      <CreateReminderModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} onSubmit={handleCreate} />
    </div>
  );
}
